// V5 P0.5 gate (post-fix): central registry of store-scoped ResourceShell routes.
//
// Each entry pairs `routePath` (which becomes `<Route path={...}>` inside the
// `/stores/:id` layout) with the lazy-loaded ShellHost component that renders
// that resource.  `App.tsx` consumes this list via `STORE_RESOURCE_ROUTES.map`
// so the eslint `crab-console-internal/store-route-registry` gate can statically
// audit every new store-scoped route: hand-written `<Route path="literal">`
// inside `App.tsx` is now caught immediately unless the path is in the
// allowlist for non-resource screens (e.g. `overview`, `reports`).
//
// To add a new resource:
//   1. Build `<feature>/<Feature>ShellHost.tsx` with `routePath: '<kebab>'` in
//      its shellConfig.
//   2. Append `{ routePath: '<kebab>', Component: lazy(...) }` here.
//   3. No change to `App.tsx` is needed.

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export interface StoreResourceRoute {
  /** Path relative to `/stores/:id/`. Matches the `routePath` declared in the
   *  feature's shellConfig so the AST gate can verify list/registry alignment. */
  routePath: string;
  /** Lazy-loaded ShellHost component (kept lazy to preserve code-split chunks). */
  Component: LazyExoticComponent<ComponentType<unknown>>;
}

export const STORE_RESOURCE_ROUTES: readonly StoreResourceRoute[] = [
  {
    routePath: 'products',
    Component: lazy(() =>
      import('@/features/product').then((m) => ({ default: m.ProductShellHost })),
    ),
  },
  {
    routePath: 'categories',
    Component: lazy(() =>
      import('@/features/category').then((m) => ({ default: m.CategoryShellHost })),
    ),
  },
  {
    routePath: 'tags',
    Component: lazy(() => import('@/features/tag').then((m) => ({ default: m.TagShellHost }))),
  },
  {
    routePath: 'attributes',
    Component: lazy(() =>
      import('@/features/attribute').then((m) => ({ default: m.AttributeShellHost })),
    ),
  },
  {
    routePath: 'price-rules',
    Component: lazy(() =>
      import('@/features/price-rule').then((m) => ({ default: m.PriceRuleShellHost })),
    ),
  },
  {
    routePath: 'promotion-rules',
    Component: lazy(() =>
      import('@/features/promotion-rule').then((m) => ({ default: m.PromotionRuleShellHost })),
    ),
  },
  {
    routePath: 'promotion-activities',
    Component: lazy(() =>
      import('@/features/promotion-activity').then((m) => ({
        default: m.PromotionActivityShellHost,
      })),
    ),
  },
  {
    routePath: 'menu-bundles',
    Component: lazy(() =>
      import('@/features/menu-bundle').then((m) => ({ default: m.MenuBundleShellHost })),
    ),
  },
  {
    routePath: 'coupon-definitions',
    Component: lazy(() =>
      import('@/features/coupon-definition').then((m) => ({
        default: m.CouponDefinitionShellHost,
      })),
    ),
  },
  {
    routePath: 'coupon-instances',
    Component: lazy(() =>
      import('@/features/coupon-instance').then((m) => ({
        default: m.CouponInstanceShellHost,
      })),
    ),
  },
  {
    routePath: 'collections',
    Component: lazy(() =>
      import('@/features/collection').then((m) => ({ default: m.CollectionShellHost })),
    ),
  },
  {
    routePath: 'employees',
    Component: lazy(() =>
      import('@/features/employee').then((m) => ({ default: m.EmployeeShellHost })),
    ),
  },
  {
    routePath: 'zones',
    Component: lazy(() => import('@/features/zone').then((m) => ({ default: m.ZoneShellHost }))),
  },
  {
    routePath: 'tables',
    Component: lazy(() => import('@/features/table').then((m) => ({ default: m.TableShellHost }))),
  },
  {
    routePath: 'table-map',
    Component: lazy(() =>
      import('@/features/table-map').then((m) => ({ default: m.TableMapEditorScreen })),
    ),
  },
  {
    routePath: 'label-templates',
    Component: lazy(() =>
      import('@/features/label-template').then((m) => ({ default: m.LabelTemplateShellHost })),
    ),
  },
  {
    routePath: 'marketing-groups',
    Component: lazy(() =>
      import('@/features/marketing-group').then((m) => ({ default: m.MarketingGroupShellHost })),
    ),
  },
  {
    routePath: 'kds-stations',
    Component: lazy(() =>
      import('@/features/kds-station').then((m) => ({ default: m.KdsStationShellHost })),
    ),
  },
  {
    routePath: 'members',
    Component: lazy(() =>
      import('@/features/member').then((m) => ({ default: m.MemberShellHost })),
    ),
  },
];
